import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDropzone } from 'react-dropzone';
import DOMPurify from 'dompurify';
import { useSaveEntry } from '../../utils/useSaveEntry';
import { linkifyText } from '@mysimpledb/shared/utils/linkifyText';
import { SafeEntryContent } from '@mysimpledb/shared/components/SafeEntryContent';
import { useDeleteEntry } from '@mysimpledb/shared/hooks/useDeleteEntry';
import {
  handleNextResult,
  handlePrevResult,
} from '@mysimpledb/shared/hooks/usePaginationHelpers';
import './EntryDetailWeb.css';
import EntryImageGallery from '../EntryImageGallery/EntryImageGallery';
import './FileUpload.css';
import { fetchEntryById } from '@mysimpledb/shared/data/fetchEntryById';
import api from '@mysimpledb/shared/config/axios';
import {
  createLinkAttachment,
  listAttachments,
} from '../../api/attachmentsClient';
import { extractUrlsFromEvent } from '../../api/attachmentsUrl';
import AppButton from '@mysimpledb/shared/components/UI/Button/Button';
import RichTextEditor from '../RichTextEditor';
import { getSignedUrl } from '../../api/attachmentsClient';
import { loadSensitiveAttachment } from '../SensitiveAttachmentBlob';
import { TimeAgo } from '@mysimpledb/shared/components/UI/TimeAgo';
import { convertFencedBlocksForRead } from '@mysimpledb/shared/utils/convertFencedBlocksForRead';
import EntryTypesCard from '../EntryTypesCard';
import '../EntryTypesCard.css';
import axios from '@mysimpledb/shared/config/axios';
import useGetUserTypes from '@mysimpledb/shared/hooks/useGetUserTypes';
import useGetPersonalInfo from '@mysimpledb/shared/hooks/useGetPersonalInfo';
import { buildAvatarUrl } from '@mysimpledb/shared/utils/mediaUrls';
import SharedPublicProfileModal from '../Account/SharedPublicProfileModal';
import { useNotify } from '@mysimpledb/shared/context/NotificationContext';
import { normalizeDetailContent } from '@mysimpledb/shared/utils/richText/normalizeDetailContent';
import { useVault } from '@mysimpledb/shared/context/VaultContext';
import VaultUnlockPanel from '@mysimpledb/shared/components/VaultUnlockPanel';
import { decryptSensitiveEntry } from '@mysimpledb/shared/utils/crypto/sensitiveEntryCrypto';
import {
  normalizeDetailHtmlWeb,
  htmlToPlainTextWeb,
} from '../../utils/normalizeDetailHtmlWeb';
import { partitionFilesByAllowedType } from '../../utils/attachmentUploadTypes';

/** Utilities */
function normalizeKeywords(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  if (!value) return [];
  return String(value)
    .split(/[,[\]\s]+|,\s*/)
    .map((t) => t.trim())
    .filter(Boolean);
}


function toCategoryTitleCase(value = '') {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/(^|[\s\-/&])([a-z0-9])/g, (_, separator, char) =>
      `${separator}${char.toUpperCase()}`
    );
}

// set-equality helper (order-insensitive)
const sameSet = (a = [], b = []) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const sa = new Set(a),
    sb = new Set(b);
  for (const v of sa) if (!sb.has(v)) return false;
  return true;
};

/** Small chip styles (inline so no extra CSS file required) */
const chipRowStyle = { display: 'flex', flexWrap: 'wrap', gap: '6px' };
const chipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  background: '#F8FAFC',
  border: '1px solid #E2E8F0',
  borderRadius: '999px',
  padding: '4px 10px',
  fontSize: '12px',
  color: '#334155',
};
const chipRemoveStyle = {
  marginLeft: '6px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  lineHeight: 1,
};
const chipInputWrap = {
  ...chipRowStyle,
  border: 'none',
  borderRadius: 'var(--input-radius)',
  padding: '0.5rem',
  minHeight: '34px',
  width: '500px',
  boxSizing: 'border-box',
  background: 'var(--input-bg)',
  alignItems: 'center',
};
const chipTextInputStyle = {
  minWidth: '80px',
  flex: '1 1 80px',
  border: 'none',
  outline: 'none',
  fontSize: '1rem',
  padding: 0,
  background: 'transparent',
};

const emptyStateCardStyle = {
  width: '100%',
  maxWidth: 900,
  margin: '40px auto',
  background: '#fff',
  border: '1px solid #d9e0ea',
  borderRadius: 18,
  padding: '34px 32px',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
};

const emptyStateTitleStyle = {
  margin: 0,
  fontSize: 28,
  fontWeight: 700,
  color: '#0f172a',
};

const emptyStateTextStyle = {
  marginTop: 12,
  marginBottom: 0,
  fontSize: 16,
  lineHeight: 1.6,
  color: '#475569',
};

export default function EntryDetailWeb({
  currentIndex,
  entries,
  entryPageNo,
  fetchPage,
  files,
  focusEntryAfterSave,
  form,
  hasLoaded,
  isEditing,
  isEmptySearch,
  itemsPerPage,
  listPageNo,
  newEntryRequest,
  searchTerm,
  searchType,
  setActiveTab,
  setCurrentIndex,
  setFiles,
  setForm,
  setIsEditing,
  setListPageNo,
  setNewEntryRequest,
  setSearchTerm,
  setSearchType,
  token,
  totalEntriesFound,
  userId,
}) {
  const [lastEntryId, setLastEntryId] = useState(null);
  const [currentEntryId, setCurrentEntryId] = useState(null);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingLinks, setPendingLinks] = useState([]); // staged links (YouTube/URLs)
  const [uploadWarning, setUploadWarning] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isSensitive, setIsSensitive] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const readOnly = !isEditing;
  const [editorHtml, setEditorHtml] = useState('');
  const [recoveryKeyConfirmed, setRecoveryKeyConfirmed] = useState(false);

  const isDesktopSyncAvailable = !!window.syncApi;
  const [keepSynced, setKeepSynced] = useState(false);

  // types catalog shown in the picker
  const [typesCatalog, setTypesCatalog] = useState([]);

  // entry’s local selections (do NOT put in form while editing)
  const [entryTypeIds, setEntryTypeIds] = useState(form.entryTypeIds || []);
  const [entrySubtypeIds, setEntrySubtypeIds] = useState(
    form.entrySubtypeIds || []
  );

  // if true, we *preview* (and later save) using profile defaults
  const [useProfileDefaults, setUseProfileDefaults] = useState(
    form.useDefaults ?? true
  );

  // optional: show chips for the user’s profile defaults
  const [profileTypeIds, setProfileTypeIds] = useState([]); // from profile
  const [profileSubtypeIds, setProfileSubtypeIds] = useState([]); // from profile

  // ensure we only auto-apply defaults once for a brand new entry
  const appliedNewDefaultsRef = useRef(false);

  // version to force gallery re-render & defeat cache (used for external URLs)
  const [galleryVersion, setGalleryVersion] = useState(0);

  // --- keywords state for web chip input ---
  const [keywordsArr, setKeywordsArr] = useState(
    normalizeKeywords(form?.keywords)
  );
  const [kwText, setKwText] = useState('');
  const kwInputRef = useRef(null);

  // Category autocomplete is based only on categories from this user's own entries.
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [categoryActiveIndex, setCategoryActiveIndex] = useState(-1);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const categoryInputRef = useRef(null);

  const [showVaultSetup, setShowVaultSetup] = useState(false);
  const [showVaultUnlock, setShowVaultUnlock] = useState(false);
  const [pendingSensitiveSave, setPendingSensitiveSave] = useState(null);
  const [newRecoveryKey, setNewRecoveryKey] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const [publicProfileUserId, setPublicProfileUserId] = useState(null);

  const handleCopyRecoveryKey = async () => {
    try {
      await navigator.clipboard.writeText(newRecoveryKey);
      setCopyMessage('Recovery key copied');
    } catch (err) {
      console.error('copy recovery key failed', err);
      setCopyMessage('Unable to copy recovery key');
    }
  };

  const categoryPillStyle = {
    ...chipStyle,
    cursor: 'pointer',
    userSelect: 'none',
  };

  const loadCategoryOptions = useCallback(async () => {
    if (!token) {
      setCategoryOptions([]);
      return;
    }

    setCategoryLoading(true);
    try {
      const { data } = await api.get('/userdatad/categories', {
        headers: { Authorization: `Bearer ${token}` },
      });

      const categories = Array.isArray(data?.categories) ? data.categories : [];
      setCategoryOptions(categories);
    } catch (err) {
      console.error('Failed to load category suggestions:', err);
      setCategoryOptions([]);
    } finally {
      setCategoryLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadCategoryOptions();
  }, [loadCategoryOptions]);

  const filteredCategoryOptions = useMemo(() => {
    const typed = String(form?.category ?? '').trim().toLowerCase();
    const options = categoryOptions.map((option) => ({
      ...option,
      displayCategory: toCategoryTitleCase(option.category),
    }));

    if (!typed) {
      return options.slice(0, 10);
    }

    const startsWith = [];
    const contains = [];

    options.forEach((option) => {
      const value = String(option.category ?? '').toLowerCase();
      if (value.startsWith(typed)) {
        startsWith.push(option);
      } else if (value.includes(typed)) {
        contains.push(option);
      }
    });

    return [...startsWith, ...contains].slice(0, 10);
  }, [categoryOptions, form?.category]);

  const chooseCategory = useCallback(
    (category) => {
      const displayCategory = toCategoryTitleCase(category);
      setForm((prev) => ({ ...prev, category: displayCategory }));
      setCategoryOpen(false);
      setCategoryActiveIndex(-1);
      categoryInputRef.current?.focus();
    },
    [setForm]
  );

  const handleCategoryChange = (e) => {
    setForm((prev) => ({ ...prev, category: e.target.value }));
    setCategoryOpen(true);
    setCategoryActiveIndex(-1);
  };

  const handleCategoryKeyDown = (e) => {
    if (!categoryOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setCategoryOpen(true);
      setCategoryActiveIndex(0);
      e.preventDefault();
      return;
    }

    if (!categoryOpen) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCategoryActiveIndex((current) =>
        Math.min(current + 1, filteredCategoryOptions.length - 1)
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCategoryActiveIndex((current) => Math.max(current - 1, 0));
    } else if (e.key === 'Enter' && categoryActiveIndex >= 0) {
      const selected = filteredCategoryOptions[categoryActiveIndex];
      if (selected) {
        e.preventDefault();
        chooseCategory(selected.category);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setCategoryOpen(false);
      setCategoryActiveIndex(-1);
    }
  };

  const notify = useNotify();

  const hasActiveSearch = Boolean(
    String(searchTerm ?? '').trim() || String(searchType ?? '').trim()
  );

  const showEmptySearchState =
    hasLoaded && !isEditing && !isNew && isEmptySearch && entries.length === 0;

  const showNoEntriesState =
    hasLoaded &&
    !isEditing &&
    !isNew &&
    !isEmptySearch &&
    !hasActiveSearch &&
    entries.length === 0 &&
    totalEntriesFound === 0;

  const {
    sensitiveKey,
    isVaultUnlocked,
    setupVault,
    vaultExists,
    unlockVaultWithRecoveryKey,
    touchVaultActivity,
  } = useVault();

  const [vaultPass, setVaultPass] = useState('');
  const [vaultPassConfirm, setVaultPassConfirm] = useState('');
  const shouldShowReadOnlyVaultUnlock =
    readOnly && form?.isSensitive && !sensitiveKey;

  const validateBeforeSave = useCallback(() => {
    const title = String(form?.title ?? '').trim();
    const category = String(form?.category ?? '').trim();
    const keywords = normalizeKeywords(keywordsArr);
    const detail = String(form?.detail ?? '').trim();

    if (!title) {
      notify.show('Title is required.');
      return false;
    }

    if (!category) {
      notify.show('Category is required.');
      return false;
    }

    if (!keywords.length) {
      notify.show('Please add at least one keyword.');
      return false;
    }

    if (!detail) {
      notify.show('Detail is required.');
      return false;
    }

    return true;
  }, [form?.title, form?.detail, keywordsArr, notify]);

  /** URL helpers */
  const isLikelyUrl = (str) => {
    try {
      const u = new URL(str.trim());
      return !!u.protocol && !!u.host;
    } catch {
      return false;
    }
  };
  function detectLinkKind(url) {
    try {
      const u = new URL(url);
      const host = (u.hostname || '').toLowerCase();
      if (
        host === 'youtu.be' ||
        host.endsWith('.youtube.com') ||
        host === 'youtube.com'
      )
        return 'youtube';
      return 'link';
    } catch {
      return 'link';
    }
  }

  // Remove inline styles from <pre> blocks and everything inside them
  function stripInlineStylesInPre(html = '') {
    if (!html) return html;
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    const pres = wrapper.querySelectorAll('pre');
    pres.forEach((pre) => {
      pre.removeAttribute('style');
      pre.querySelectorAll('*').forEach((el) => {
        el.removeAttribute('style');
      });
    });

    return wrapper.innerHTML;
  }

  function formatBytes(bytes) {
    const n = Number(bytes || 0);
    if (!Number.isFinite(n)) return '0 Bytes';

    const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    let value = n;
    let unit = 0;

    while (value >= 1024 && unit < units.length - 1) {
      value /= 1024;
      unit++;
    }

    return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
  }

  function getFriendlyUploadError(err) {
    const data = err?.response?.data || {};
    const code = err?.code || data?.code;
    const status = err?.status || err?.response?.status;

    if (code === 'STORAGE_LIMIT_REACHED' || status === 413) {
      const used = err?.storageUsedBytes ?? data?.storageUsedBytes;
      const limit = err?.storageLimitBytes ?? data?.storageLimitBytes;
      const incoming = err?.incomingBytes ?? data?.incomingBytes;

      return [
        'Storage limit reached.',
        '',
        `Storage used: ${formatBytes(used)} of ${formatBytes(limit)}.`,
        `This upload requires ${formatBytes(incoming)}.`,
        '',
        'Delete attachments or upgrade your account for more storage.',
      ].join('\n');
    }

    return data?.error || err?.message || 'Save failed.';
  }

  // Size limits for attachments
  const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB per file
  const MAX_TOTAL_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB per entry

  function partitionFilesBySize(incomingFiles = [], existingFiles = []) {
    let currentTotal = existingFiles.reduce((sum, f) => sum + (f.size || 0), 0);

    const allowed = [];
    const rejected = [];

    for (const file of incomingFiles) {
      const size = file.size ?? 0;

      if (size > MAX_FILE_SIZE_BYTES) {
        rejected.push({
          file,
          reason: `File exceeds max size of 10 MB (this one is ${(
            size /
            (1024 * 1024)
          ).toFixed(1)} MB).`,
        });
        continue;
      }

      if (currentTotal + size > MAX_TOTAL_SIZE_BYTES) {
        rejected.push({
          file,
          reason: `Adding this file would exceed the 50 MB total limit for this entry.`,
        });
        continue;
      }

      allowed.push(file);
      currentTotal += size;
    }

    return { allowed, rejected };
  }

  /* =========================
     HOOKS FOR TYPES + PROFILE
     ========================= */

  const {
    data: userTypesData,
    loading: userTypesLoading,
    error: userTypesError,
  } = useGetUserTypes(token);

  const {
    data: personalInfo,
    loading: personalLoading,
    error: personalError,
  } = useGetPersonalInfo(token);

  useEffect(() => {
    const catalog = userTypesData?.types ?? userTypesData ?? [];
    if (Array.isArray(catalog)) setTypesCatalog(catalog);
  }, [userTypesData]);

  useEffect(() => {
    const t = personalInfo?.allowedTypeIds ?? [];
    const st = personalInfo?.allowedSubtypeIds ?? [];
    setProfileTypeIds(Array.isArray(t) ? t : []);
    setProfileSubtypeIds(Array.isArray(st) ? st : []);
  }, [personalInfo]);

  useEffect(() => {
    if (!isEditing) return;
    const isBrandNew = !form?.id;
    if (!isBrandNew) return;
    if (appliedNewDefaultsRef.current) return;
    if (profileTypeIds.length === 0 && profileSubtypeIds.length === 0) return;

    setEntryTypeIds(profileTypeIds);
    setEntrySubtypeIds(profileSubtypeIds);
    setUseProfileDefaults(true);
    appliedNewDefaultsRef.current = true;
  }, [isEditing, form?.id, profileTypeIds, profileSubtypeIds]);

  useEffect(() => {
    if (!form?.id) return;
    const matches =
      sameSet(entryTypeIds, profileTypeIds) &&
      sameSet(entrySubtypeIds, profileSubtypeIds);
    setUseProfileDefaults(matches);
  }, [
    form?.id,
    entryTypeIds,
    entrySubtypeIds,
    profileTypeIds,
    profileSubtypeIds,
  ]);

  const handleToggleUseDefaults = (next) => {
    setUseProfileDefaults(next);
    if (next) {
      setEntryTypeIds(profileTypeIds || []);
      setEntrySubtypeIds(profileSubtypeIds || []);
    }
  };

  /** list navigation after clicking pill/chip */
  const [pendingListNav, setPendingListNav] = React.useState(false);
  React.useEffect(() => {
    if (!pendingListNav) return;
    (async () => {
      await fetchPage(0);
      setActiveTab('list');
      setPendingListNav(false);
    })();
  }, [pendingListNav, fetchPage, searchTerm, searchType]);

  const handleCategoryClick = (name) => {
    if (!name) return;
    setSearchTerm(name);
    setSearchType('category');
    setPendingListNav(true);
  };

  const handleKeywordClick = useCallback((kw) => {
    const name = String(kw ?? '').trim();
    if (!name) return;
    setSearchTerm(name);
    setSearchType('keyword');
    setPendingListNav(true);
  }, []);

  const handleClearSearch = useCallback(async () => {
    setSearchTerm('');
    setSearchType('');
    setCurrentIndex(0);
    setListPageNo(0);
    await fetchPage(0);
  }, [fetchPage, setCurrentIndex, setListPageNo, setSearchTerm, setSearchType]);

  useEffect(() => {
    setKeywordsArr(normalizeKeywords(form?.keywords));
  }, [form?.keywords, form?.id]);

  const commitTokens = (raw) => {
    const toks = String(raw || '')
      .toLowerCase()
      .trim()
      .split(/[,\s]+/)
      .map((t) => t.replace(/[^a-z0-9-]/gi, ''))
      .filter(Boolean);
    if (!toks.length) return;
    const set = new Set(Array.isArray(keywordsArr) ? keywordsArr : []);
    toks.forEach((t) => set.add(t));
    const next = Array.from(set);
    setKeywordsArr(next);
    setForm({ ...form, keywords: next });
  };

  const handleKeywordRemove = (idx) => {
    const next = keywordsArr.slice();
    next.splice(idx, 1);
    setKeywordsArr(next);
    setForm({ ...form, keywords: next });
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === ',') {
      e.preventDefault();
      commitTokens(kwText);
      setKwText('');
    } else if (
      e.key === 'Backspace' &&
      kwText.length === 0 &&
      keywordsArr.length > 0
    ) {
      handleKeywordRemove(keywordsArr.length - 1);
    }
  };

  const handleKeywordBlur = () => {
    if (kwText.trim()) {
      commitTokens(kwText);
      setKwText('');
    }
  };

  function addFilesWithUniqueNames(incomingFiles, prevFiles) {
    const taken = new Set(prevFiles.map((f) => f.uploadName || f.name));
    const output = [...prevFiles];

    incomingFiles.forEach((file) => {
      const match = file.name.match(/^(.*?)(\.[^.]+)?$/);
      const base = match ? match[1] : file.name;
      const ext = match && match[2] ? match[2] : '';

      let uploadName = file.name;
      let i = 1;

      while (taken.has(uploadName)) {
        uploadName = `${base}_${i}${ext}`;
        i += 1;
      }

      taken.add(uploadName);

      // IMPORTANT:
      // Preserve original Electron File object so file.path survives.
      // Add separate uploadName property instead of cloning File.
      file.uploadName = uploadName;

      output.push(file);
    });

    return output;
  }

  const onDrop = useCallback(
    async (acceptedFiles, _rejections, event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();

      try {
        const urls = await extractUrlsFromEvent(event);
        if (urls.length) {
          setPendingLinks((prev) => {
            const s = new Set(prev);
            urls.forEach((u) => s.add(u));
            return Array.from(s);
          });
        }

        if (acceptedFiles?.length) {
          const droppedFiles = Array.from(event?.dataTransfer?.files || []);

          console.log(
            '[sync-drop-native]',
            droppedFiles.map((f) => ({
              name: f.name,
              path: f.path,
              localPath: f.localPath,
            }))
          );
          let filesToAdd = acceptedFiles;

          if (keepSynced && window.syncApi?.getDroppedFilePaths) {
            const electronPaths = window.syncApi.getDroppedFilePaths();

            filesToAdd = acceptedFiles
              .map((file, index) => {
                const localPath = electronPaths[index];

                if (!localPath || !localPath.startsWith('/')) {
                  setUploadWarning(
                    'Synced drag/drop needs the real local file path. Please use Browse Files for synced uploads until drag/drop path capture is working.'
                  );
                  return null;
                }

                file.localPath = localPath;
                file.uploadName = file.uploadName || file.name;
                return file;
              })
              .filter(Boolean);

            if (!filesToAdd.length) return;
          }

          const { allowed: typeAllowed, rejected: typeRejected } =
            partitionFilesByAllowedType(filesToAdd);
          const { allowed, rejected: sizeRejected } = partitionFilesBySize(
            typeAllowed,
            pendingFiles
          );

          if (allowed.length > 0) {
            setPendingFiles((prev) => addFilesWithUniqueNames(allowed, prev));
          }

          const warnings = [];
          if (typeRejected.length > 0) {
            const names = typeRejected.map((r) => r.file.name).join(', ');
            warnings.push(
              `Unsupported attachment${
                typeRejected.length === 1 ? '' : 's'
              } skipped: ${names}.`
            );
          }

          if (sizeRejected.length > 0) {
            const names = sizeRejected.map((r) => r.file.name).join(', ');
            warnings.push(
              `${
                sizeRejected.length === 1 ? 'The file is' : 'These files are'
              } too large and were not added: ${names}. ` +
                'Each attachment must be smaller than 10 MB, and the total per entry under 50 MB.'
            );
          }

          setUploadWarning(warnings.join(' '));
        }
      } catch (err) {
        console.error('onDrop error', err);
      }
    },
    [pendingFiles, setPendingLinks, setPendingFiles, keepSynced]
  );

  const handlePaste = useCallback(
    (e) => {
      const cd = e.clipboardData || window.clipboardData;
      if (!cd) return;

      const files = [];
      for (let i = 0; i < cd.items.length; i++) {
        const it = cd.items[i];
        if (it.kind === 'file') {
          const f = it.getAsFile?.();
          if (f && f.type && f.type.startsWith('image/')) {
            files.push(f);
          }
        }
      }

      if (files.length) {
        e.preventDefault();

        const { allowed, rejected } = partitionFilesBySize(files, pendingFiles);

        if (allowed.length > 0) {
          setPendingFiles((prev) => addFilesWithUniqueNames(allowed, prev));
        }

        if (rejected.length > 0) {
          const names = rejected.map((r) => r.file.name).join(', ');
          setUploadWarning(
            `The pasted image "${names}" is too large to upload. Each attachment must be smaller than 10 MB (and the total per entry under 50 MB).`
          );
        } else {
          setUploadWarning('');
        }

        return;
      }

      const text = cd.getData('text')?.trim();
      if (text) {
        try {
          const u = new URL(text);
          setPendingLinks((prev) =>
            Array.from(new Set([...prev, u.toString()]))
          );
        } catch (_) {
          /* not a URL — ignore */
        }
      }
    },
    [setPendingFiles, setPendingLinks]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
  });

  const dropzoneRef = useRef(null);
  const rootProps = getRootProps({
    ref: dropzoneRef,
    tabIndex: 0,
    className: 'upload-box',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'keywords') return;
    setForm({ ...form, [name]: value });
  };

  const handleEdit = async () => {
    if (form?.isSensitive && !sensitiveKey) {
      const exists = await vaultExists();

      if (exists) {
        notify.show('Unlock your vault to edit this sensitive entry.');
        setShowVaultUnlock(true);
        setShowVaultSetup(false);
      } else {
        setShowVaultSetup(true);
        setShowVaultUnlock(false);
      }

      return;
    }

    if (form?.isSensitive) {
      touchVaultActivity();
    }

    setLastEntryId(form?.id);
    setIsPublic(form?.isPublic);
    setIsSensitive(form?.isSensitive);
    setIsEditing(true);
    setKeywordsArr(normalizeKeywords(form?.keywords));
  };

  /** ------------------ DETAIL helpers (UPDATED) ------------------ */

  const textToHtml = (text = '') => {
    const normalized = String(text).replace(/\r\n?/g, '\n');

    const escaped = normalized
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    const linked = escaped
      .replace(
        /((?:https?:\/\/|ftp:\/\/)[^\s<]+[^<.,:;"')\]\s])/gi,
        '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
      )
      .replace(
        /(^|[\s(])((?:www\.)[^\s<]+[^<.,:;"')\]\s])/gi,
        '$1<a href="http://$2" target="_blank" rel="noopener noreferrer">$2</a>'
      )
      .replace(
        /(^|[\s(])([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi,
        '$1<a href="mailto:$2">$2</a>'
      );

    const withBreaks = linked.replace(/\n/g, '<br/>');
    return `<p>${withBreaks}</p>`;
  };

  function forceAnchorTargetBlank(html = '') {
    let out = html.replace(/<a\b(?![^>]*\btarget=)[^>]*?>/gi, (m) =>
      m.replace(/>$/, ' target="_blank" rel="noopener noreferrer">')
    );
    out = out.replace(/(<a\b[^>]*\btarget=)(["'])?_self\2?/gi, '$1"_blank"');
    return out;
  }

  function linkifyBareUrlsInHtml(html = '') {
    if (!html) return html;

    const urlRe = /((?:https?:\/\/|ftp:\/\/)[^\s<]+[^<.,:;"')\]\s])/gi;
    const wwwRe = /(^|[\s(])((?:www\.)[^\s<]+[^<.,:;"')\]\s])/gi;
    const emailRe = /(^|[\s(])([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;

    return html
      .split(/(<[^>]+>)/g)
      .map((part) => {
        if (part.startsWith('<')) return part;
        return part
          .replace(
            urlRe,
            '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
          )
          .replace(
            wwwRe,
            '$1<a href="http://$2" target="_blank" rel="noopener noreferrer">$2</a>'
          )
          .replace(emailRe, '$1<a href="mailto:$2">$2</a>');
      })
      .join('');
  }

  // Link bare URLs in normalized rich HTML without changing saved detailHtml.
  // Existing anchors and code/preformatted content are deliberately skipped.
  function linkifyBareUrlsInStructuredHtml(html = '') {
    if (!html) return html;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    const tokenRe =
      /(?:https?:\/\/|ftp:\/\/|www\.)[^\s<]+|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
    const trailingPunctuationRe = /[.,;:!?)}\]]+$/;

    const walker = document.createTreeWalker(wrapper, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent || !node.nodeValue?.trim()) {
          return NodeFilter.FILTER_REJECT;
        }

        if (parent.closest('a, pre, code, script, style, textarea')) {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const textNodes = [];
    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    textNodes.forEach((textNode) => {
      const text = textNode.nodeValue || '';
      tokenRe.lastIndex = 0;

      let match;
      let cursor = 0;
      let changed = false;
      const fragment = document.createDocumentFragment();

      while ((match = tokenRe.exec(text)) !== null) {
        const rawToken = match[0];
        const trailing = rawToken.match(trailingPunctuationRe)?.[0] || '';
        const token = trailing
          ? rawToken.slice(0, rawToken.length - trailing.length)
          : rawToken;

        if (!token) continue;

        if (match.index > cursor) {
          fragment.appendChild(
            document.createTextNode(text.slice(cursor, match.index))
          );
        }

        const anchor = document.createElement('a');
        anchor.textContent = token;
        anchor.href =
          token.includes('@') && !/^\w+:\/\//i.test(token)
            ? `mailto:${token}`
            : /^www\./i.test(token)
            ? `http://${token}`
            : token;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        fragment.appendChild(anchor);

        if (trailing) {
          fragment.appendChild(document.createTextNode(trailing));
        }

        cursor = match.index + rawToken.length;
        changed = true;
      }

      if (!changed) return;

      if (cursor < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(cursor)));
      }

      textNode.parentNode?.replaceChild(fragment, textNode);
    });

    return wrapper.innerHTML;
  }

  const initialHtml =
    form.detailHtml && form.detailHtml.trim().length
      ? form.detailHtml
      : form.detail && form.detail.trim().length
      ? textToHtml(form.detail)
      : '';

  useEffect(() => {
    if (!isEditing) return;
    setEditorHtml(initialHtml || '');
  }, [isEditing, form?.id, initialHtml]);

  function restoreLineBreaksOutsidePre(html) {
    if (!html) return '';

    return String(html)
      .split(/(<pre[\s\S]*?<\/pre>)/gi)
      .map((chunk, i) => {
        if (i % 2 === 1) return chunk;

        const cleaned = chunk
          .replace(/>\s*\n\s*</g, '><')
          .replace(/>\s+</g, '><');

        const paragraphized = cleaned.replace(/\n{2,}/g, '<br><br>');
        return paragraphized.replace(/\n/g, '<br>');
      })
      .join('');
  }

  function normalizePreBlocks(html = '') {
    if (!html) return html;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    const blockLike = new Set([
      'DIV',
      'P',
      'LI',
      'UL',
      'OL',
      'H1',
      'H2',
      'H3',
      'H4',
      'H5',
      'H6',
      'BLOCKQUOTE',
    ]);

    const extractPreTextPreservingBreaks = (node) => {
      let out = '';

      const walk = (n) => {
        if (n.nodeType === Node.TEXT_NODE) {
          out += n.textContent || '';
          return;
        }

        if (n.nodeType !== Node.ELEMENT_NODE) return;

        const el = n;

        if (el.tagName === 'BR') {
          out += '\n';
          return;
        }

        const isBlock = blockLike.has(el.tagName);
        const needsLeadingBreak =
          isBlock && out.length > 0 && !out.endsWith('\n');

        if (needsLeadingBreak) {
          out += '\n';
        }

        for (const child of el.childNodes) {
          walk(child);
        }

        if (isBlock && !out.endsWith('\n')) {
          out += '\n';
        }
      };

      for (const child of node.childNodes) {
        walk(child);
      }

      return out
        .replace(/\r\n?/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\s+$/g, '');
    };

    wrapper.querySelectorAll('pre').forEach((pre) => {
      const text = extractPreTextPreservingBreaks(pre);

      while (pre.firstChild) pre.removeChild(pre.firstChild);
      [...pre.attributes].forEach((attr) => pre.removeAttribute(attr.name));

      const codeEl = document.createElement('code');
      codeEl.textContent = text;
      pre.appendChild(codeEl);
    });

    return wrapper.innerHTML;
  }

  function compactHtmlForRead(html = '') {
    const stripEdges = (s) =>
      s
        .replace(
          /^(?:\s*(?:<(?:p|div)[^>]*>\s*(?:&nbsp;|\s|<br\s*\/?>)*<\/(?:p|div)>|<br\s*\/?>)+)+/i,
          ''
        )
        .replace(
          /(?:\s*(?:<(?:p|div)[^>]*>\s*(?:&nbsp;|\s|<br\s*\/?>)*<\/(?:p|div)>|<br\s*\/?>)+)+\s*$/i,
          ''
        );

    let out = stripEdges(html);

    out = out
      .replace(
        /<(?:div|p)[^>]*>\s*(?:&nbsp;|\s|<br\s*\/?>)*<\/(?:div|p)>/gi,
        '<br>'
      )
      .replace(/(?:\s*<br\s*\/?>\s*){3,}/gi, '<br><br>');

    out = out
      .replace(/<(ul|ol)(\s[^>]*)?>\s*(?:<br\s*\/?>\s*)+/gi, '<$1$2>')
      .replace(
        /<li(\s[^>]*)?>\s*<(p|div)(\s[^>]*)?>([\s\S]*?)<\/\2>\s*<\/li>/gi,
        '<li$1>$4</li>'
      )
      .replace(/<\/li>\s*(?:<br\s*\/?>|\n|\r|\t|\f)+\s*<li/gi, '</li><li')
      .replace(/<\/(ol|ul)>\s*(?:<br\s*\/?>|\n|\r|\t|\f)+/gi, '</$1>')
      .replace(
        /<\/h([1-6])>\s*(?:(?:<(?:p|div)[^>]*>\s*(?:&nbsp;|\s|<br\s*\/?>)*<\/(?:p|div)>)|<br\s*\/?>|\n|\r|\t|\f)+(?=\s*<(?:ol|ul)\b)/gi,
        '</h$1>'
      )
      .trim();

    return out;
  }

  const hasStructuredHtml =
    /<(p|div|table|ul|ol|li|pre|blockquote|h[1-6]|a|strong|em|code)\b/i.test(
      initialHtml || ''
    );

  const preparedHtml = useMemo(() => {
    const base = normalizePreBlocks(initialHtml || '');

    if (hasStructuredHtml) {
      return forceAnchorTargetBlank(linkifyBareUrlsInStructuredHtml(base));
    }

    const fenced = convertFencedBlocksForRead
      ? convertFencedBlocksForRead(base)
      : base;

    const linkified = linkifyBareUrlsInHtml
      ? linkifyBareUrlsInHtml(fenced)
      : fenced;

    return restoreLineBreaksOutsidePre(forceAnchorTargetBlank(linkified));
  }, [initialHtml, hasStructuredHtml]);

  const finalHtml = useMemo(() => {
    return DOMPurify.sanitize(preparedHtml, {
      ALLOWED_TAGS: [
        'p',
        'br',
        'a',
        'strong',
        'b',
        'em',
        'i',
        'u',
        's',
        'span',
        'div',
        'ul',
        'ol',
        'li',
        'blockquote',
        'pre',
        'code',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'table',
        'thead',
        'tbody',
        'tfoot',
        'tr',
        'th',
        'td',
        'caption',
        'colgroup',
        'col',
        'img',
      ],
      ALLOWED_ATTR: [
        'href',
        'target',
        'rel',
        'class',
        'src',
        'alt',
        'title',
        'colspan',
        'rowspan',
        'scope',
      ],
    });
  }, [preparedHtml]);

  const stripLeadingTrailingEmpties = (s = '') =>
    s
      .replace(
        /^(?:\s*(?:<(?:p|div)[^>]*>\s*(?:&nbsp;|\s|<br\s*\/?>)*<\/(?:p|div)>|\s*<br\s*\/?>)+)+/i,
        ''
      )
      .replace(
        /(?:\s*(?:<(?:p|div)[^>]*>\s*(?:&nbsp;|\s|<br\s*\/?>)*<\/(?:p|div)>|\s*<br\s*\/?>)+)+\s*$/i,
        ''
      );

  const decodeEntities = (s = '') => {
    const el = document.createElement('textarea');
    el.innerHTML = s;
    return el.value;
  };

  const htmlToPlainText = (html = '') => {
    const prepped = String(html)
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>\s*<p>/gi, '\n\n')
      .replace(/<\/(div|li)>/gi, '\n')
      .replace(/<\/h[1-6]>/gi, '\n');

    const plain = DOMPurify.sanitize(prepped, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    });

    return plain
      .replace(/\u00A0/g, ' ')
      .replace(/\r\n?/g, '\n')
      .trimEnd();
  };

  const isMeaningfulMarkup = (html = '') =>
    /<(p|br|b|strong|i|em|u|s|strike|ul|ol|li|blockquote|pre|code|h[1-6]|a|span|div)\b/i.test(
      html
    );

  const ALLOWED_HTML_TAGS = [
    'p',
    'br',
    'a',
    'strong',
    'em',
    'u',
    's',
    'span',
    'div',
    'ul',
    'ol',
    'li',
    'blockquote',
    'pre',
    'code',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
  ];

  const ALLOWED_HTML_ATTRS = ['href', 'target', 'rel', 'class'];

  const hydrateEntryForDisplay = useCallback(
    async (entry) => {
      if (!entry) return entry;

      if (!entry.is_sensitive) {
        return entry;
      }

      if (!sensitiveKey) {
        return {
          ...entry,
          detail: '[Sensitive entry locked]',
          detail_html: '<p><em>Sensitive entry locked</em></p>',
        };
      }

      try {
        const decrypted = await decryptSensitiveEntry({
          entry,
          key: sensitiveKey,
        });

        touchVaultActivity();

        return {
          ...entry,
          detail: decrypted.detail,
          detail_html: decrypted.detail_html ?? decrypted.detailHtml,
        };
      } catch (err) {
        console.error('Failed to decrypt sensitive entry:', err);
        return {
          ...entry,
          detail: '[Unable to decrypt sensitive entry]',
          detail_html: '<p><em>Unable to decrypt sensitive entry</em></p>',
        };
      }
    },
    [sensitiveKey, touchVaultActivity]
  );

  const refreshEntryAndGallery = useCallback(async () => {
    const id = form?.id;
    if (!id) return;
    try {
      const [fresh, freshAttachments] = await Promise.all([
        fetchEntryById(id, token),
        listAttachments(id, token),
      ]);

      const displayEntry = await hydrateEntryForDisplay(fresh);

      if (displayEntry) {
        setForm((prev) => ({
          ...prev,
          id: displayEntry.id,
          category: displayEntry.category,
          title: displayEntry.title,
          keywords: displayEntry.keywords,
          detail: displayEntry.detail,
          detailHtml: displayEntry.detail_html,
          isPublic: displayEntry.is_public,
          isSensitive: displayEntry.is_sensitive,
          sharedName: displayEntry.shared_name,
          shareProfilePicture: displayEntry.share_profile_picture,
          profilePicture: displayEntry.profile_picture,
          dataOwnerId: displayEntry.user_id,
          imageUrls: displayEntry.image_urls,
          updatedAt: displayEntry.updated_at,
        }));
        setFiles(Array.isArray(freshAttachments) ? freshAttachments : []);
        setGalleryVersion((v) => v + 1);
      }
    } catch (e) {
      console.error('refreshEntryAndGallery failed', e);
    }
  }, [form?.id, setForm, token, hydrateEntryForDisplay, sensitiveKey]);

  const saveEntry = useSaveEntry();

  const updateAttachmentSyncInfo = useCallback(
    async ({
      attachmentId,
      syncEnabled = true,
      syncStatus = 'synced',
      deviceId = null,
      deviceName = 'MySimpleDB Desktop',
      lastLocalModifiedAt = null,
    }) => {
      if (!attachmentId) return null;

      return api.post(`/attachments/${attachmentId}/sync-info`, {
        sync_enabled: syncEnabled,
        sync_status: syncStatus,
        sync_device_id: deviceId,
        sync_device_name: deviceName,
        last_local_modified_at: lastLocalModifiedAt,
      });
    },
    []
  );

  const saveSnapshot = useCallback(
    async (snapshot, keyToUse) => {
      if (isSensitive && pendingLinks.length) {
        notify.show(
          'Links and YouTube attachments are not yet supported for sensitive entries.',
          6000
        );
        return;
      }

      if (isSensitive && keepSynced) {
        notify.show(
          'Keep Synced is unavailable for sensitive attachments.',
          6000
        );
        return;
      }

      const normalized = normalizeDetailContent(snapshot.detailHtml || '', {
        normalizeHtmlImpl: normalizeDetailHtmlWeb,
        htmlToPlainTextImpl: htmlToPlainTextWeb,
      });

      const payloadForm = {
        ...form,
        id: snapshot.id ?? null,
        category: snapshot.category,
        title: snapshot.title,
        keywords: normalizeKeywords(snapshot.keywords),
        detailHtml: normalized.detailHtml,
        detail: normalized.detail,
        isPublic: snapshot.isPublic,
        isSensitive: snapshot.isSensitive,
        entryTypeIds: snapshot.entryTypeIds || [],
        entrySubtypeIds: snapshot.entrySubtypeIds || [],
      };

      setForm((prev) => ({
        ...prev,
        ...payloadForm,
      }));
      setIsPublic(Boolean(snapshot.isPublic));
      setIsSensitive(Boolean(snapshot.isSensitive));
      setEntryTypeIds(snapshot.entryTypeIds || []);
      setEntrySubtypeIds(snapshot.entrySubtypeIds || []);
      setKeywordsArr(normalizeKeywords(snapshot.keywords));
      setEditorHtml(snapshot.detailHtml || '');

      if (snapshot.isSensitive && (snapshot.pendingLinks || []).length) {
        notify.show(
          'Links and YouTube attachments are not yet supported for sensitive entries.',
          6000
        );
        return;
      }

      let savedId;

      if (snapshot.isNewEntry) {
        setGalleryImages([]);
        setFiles([]);
      }

      try {
        savedId = await saveEntry({
          form: payloadForm,
          isPublic: snapshot.isPublic,
          isSensitive: snapshot.isSensitive,
          keepSynced,
          pendingFiles,
          setCurrentEntryId,
          setIsEditing,
          setPendingFiles,
          token,
          sensitiveKey: snapshot.isSensitive ? keyToUse : null,
          userId,
          onSyncedAttachmentTracked: updateAttachmentSyncInfo,
        });

        if (snapshot.isSensitive) {
          touchVaultActivity();
        }
      } catch (err) {
        console.error('save entry failed', err);
        notify.show(getFriendlyUploadError(err), 6000);
        return;
      }

      if (savedId && (snapshot.pendingLinks || []).length) {
        for (const url of snapshot.pendingLinks) {
          try {
            await createLinkAttachment(savedId, url, token);
          } catch (e) {
            console.error('Failed to create link attachment', url, e);
          }
        }
      }

      setPendingLinks([]);
      setPendingSensitiveSave(null);
      setShowVaultSetup(false);
      setShowVaultUnlock(false);
      setVaultPass('');
      setVaultPassConfirm('');

      await refreshEntryAndGallery();
      await loadCategoryOptions();
      await focusEntryAfterSave?.(savedId);

      if (snapshot.isNewEntry || isNew) {
        setIsNew(false);
      }

      return savedId;
    },
    [
      currentEntryId,
      entryPageNo,
      fetchPage,
      form,
      isNew,
      loadCategoryOptions,
      refreshEntryAndGallery,
      saveEntry,
      setForm,
      token,
      keepSynced,
      touchVaultActivity,
    ]
  );

  const resumePendingSensitiveSave = useCallback(
    async (keyOverride) => {
      if (!pendingSensitiveSave) return;
      await saveSnapshot(pendingSensitiveSave, keyOverride || sensitiveKey);
    },
    [pendingSensitiveSave, saveSnapshot, sensitiveKey]
  );

  const handleSubmit = async () => {
    if (!validateBeforeSave()) return;

    if (isSensitive && !sensitiveKey) {
      const exists = await vaultExists();

      if (form?.id) {
        if (exists) {
          setShowVaultUnlock(true);
          setShowVaultSetup(false);
        } else {
          setShowVaultSetup(true);
          setShowVaultUnlock(false);
        }
        return;
      }

      setPendingSensitiveSave({
        id: null,
        category: form.category,
        title: form.title,
        keywords: normalizeKeywords(keywordsArr),
        detail: form.detail,
        detailHtml: editorHtml || form.detailHtml || '',
        isPublic,
        isSensitive,
        entryTypeIds,
        entrySubtypeIds,
        pendingFiles: [...pendingFiles],
        pendingLinks: [...pendingLinks],
        isNewEntry: true,
      });

      if (exists) {
        setShowVaultUnlock(true);
        setShowVaultSetup(false);
      } else {
        setShowVaultSetup(true);
        setShowVaultUnlock(false);
      }

      return;
    }

    if (isSensitive && pendingLinks.length) {
      notify.show(
        'Links and YouTube attachments are not yet supported for sensitive entries.',
        6000
      );
      return;
    }

    if (isSensitive && keepSynced) {
      notify.show(
        'Keep Synced is unavailable for sensitive attachments.',
        6000
      );
      return;
    }

    const normalized = normalizeDetailContent(
      editorHtml || form.detailHtml || '',
      {
        normalizeHtmlImpl: normalizeDetailHtmlWeb,
        htmlToPlainTextImpl: htmlToPlainTextWeb,
      }
    );

    const payloadForm = {
      ...form,
      detailHtml: normalized.detailHtml,
      detail: normalized.detail,
      keywords: normalizeKeywords(keywordsArr),
      entryTypeIds,
      entrySubtypeIds,
    };

    let savedId;

    if (isNew) {
      setGalleryImages([]);
      setFiles([]);
    }

    try {
      savedId = await saveEntry({
        form: payloadForm,
        isPublic,
        isSensitive,
        keepSynced,
        pendingFiles,
        setCurrentEntryId,
        setIsEditing,
        setPendingFiles,
        token,
        sensitiveKey: isSensitive ? sensitiveKey : null,
        userId,
        onSyncedAttachmentTracked: updateAttachmentSyncInfo,
      });

      if (isSensitive) {
        touchVaultActivity();
      }
    } catch (err) {
      console.error('save entry failed', err);
      notify.show(getFriendlyUploadError(err), 6000);
      return;
    }

    if (savedId && pendingLinks.length) {
      for (const url of pendingLinks) {
        try {
          await createLinkAttachment(savedId, url, token);
        } catch (e) {
          console.error('Failed to create link attachment', url, e);
        }
      }

      await fetchPage?.(entryPageNo);
      setPendingLinks([]);
    }

    await refreshEntryAndGallery();
    await loadCategoryOptions();
    await focusEntryAfterSave?.(savedId);

    if (isNew) {
      setIsNew(false);
    }
  };

  const handleCancel = async () => {
    setIsNew(false);
    setIsEditing(false);
    setShowVaultSetup(false);
    setShowVaultUnlock(false);
    setPendingSensitiveSave(null);
    setVaultPass('');
    setVaultPassConfirm('');

    setPendingFiles([]);
    setPendingLinks([]);
    setFilesToUpload([]);
    setEditorHtml('');

    const idToRestore = lastEntryId || form?.id;
    if (idToRestore) {
      try {
        const entry = await fetchEntryById(idToRestore, token);
        const displayEntry = await hydrateEntryForDisplay(entry);

        if (displayEntry) {
          setForm({
            id: displayEntry.id,
            category: displayEntry.category,
            title: displayEntry.title,
            keywords: displayEntry.keywords,
            detail: displayEntry.detail,
            detailHtml: displayEntry.detail_html,
            isPublic: displayEntry.is_public,
            isSensitive: displayEntry.is_sensitive,
            sharedName: displayEntry.shared_name,
            shareProfilePicture: displayEntry.share_profile_picture,
            profilePicture: displayEntry.profile_picture,
            dataOwnerId: displayEntry.user_id,
            imageUrls: displayEntry.image_urls,
            updatedAt: displayEntry.updated_at,
          });
          setKeywordsArr(normalizeKeywords(displayEntry.keywords));
          setGalleryVersion((v) => v + 1);
        }
      } catch (e) {
        console.error('Cancel restore failed', e);
      }
    } else {
      setForm({
        id: null,
        category: '',
        title: '',
        keywords: [],
        detail: '',
        detailHtml: '',
        isPublic: false,
        isSensitive: false,
        shareName: '',
        shareProfilePicture: false,
        profilePicture: '',
        dataOwnerId: userId,
        updatedAt: null,
        imageUrls: [],
      });
      setKeywordsArr([]);
      setGalleryVersion((v) => v + 1);
    }
  };

  const deleteEntry = useDeleteEntry();

  const handleDelete = async () => {
    if (!form.id) return;
    const confirmed = window.confirm(
      'Are you sure you want to delete this entry?'
    );
    if (!confirmed) return;

    await deleteEntry(form.id, token);
    fetchPage(entryPageNo);
    setCurrentIndex(0);
    setPendingFiles([]);
    setPendingLinks([]);
    setFilesToUpload([]);
    setIsEditing(false);
    setEditorHtml('');
  };

  const handleNew = () => {
    setIsNew(true);
    setIsEditing(true);
    setShowVaultSetup(false);
    setShowVaultUnlock(false);
    setPendingSensitiveSave(null);
    setVaultPass('');
    setVaultPassConfirm('');

    setForm({
      id: null,
      category: '',
      title: '',
      keywords: [],
      detail: '',
      detailHtml: '',
      isPublic: false,
      isSensitive: false,
      shareName: '',
      shareProfilePicture: false,
      profilePicture: '',
      dataOwnerId: userId,
      updatedAt: null,
      imageUrls: [],
    });
    setKeywordsArr([]);
    setFiles([]);
    setGalleryImages([]);
    setLastEntryId(entries[currentIndex]?.id || null);
    setCurrentEntryId(null);
    setFilesToUpload([]);
    setPendingFiles([]);
    setPendingLinks([]);
    setUploadWarning('');
    setIsPublic(false);
    setIsSensitive(false);
    setEntryTypeIds([]);
    setEntrySubtypeIds([]);
    setEditorHtml('');
    appliedNewDefaultsRef.current = false;

    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  };

  useEffect(() => {
    if (!newEntryRequest) return;

    handleNew();

    // Consume the one-time request so it does not run again
    // when EntryDetailWeb remounts later.
    setNewEntryRequest(0);
  }, [newEntryRequest, setNewEntryRequest]);

  /** fetch + set form on entry change */
  useEffect(() => {
    if (isNew) return;

    const id = entries[currentIndex]?.id;
    if (!id) return;

    (async () => {
      const entry = await fetchEntryById(id, token);
      const displayEntry = await hydrateEntryForDisplay(entry);

      if (displayEntry) {
        setForm({
          id: displayEntry.id,
          category: displayEntry.category,
          title: displayEntry.title,
          keywords: displayEntry.keywords,
          detail: displayEntry.detail,
          detailHtml: displayEntry.detail_html,
          isPublic: displayEntry.is_public,
          isSensitive: displayEntry.is_sensitive,
          sharedName: displayEntry.shared_name,
          shareProfilePicture: displayEntry.share_profile_picture,
          profilePicture: displayEntry.profile_picture,
          dataOwnerId: displayEntry.user_id,
          imageUrls: displayEntry.image_urls,
          updatedAt: displayEntry.updated_at,
        });

        setEntryTypeIds(displayEntry.entry_type_ids || []);
        setEntrySubtypeIds(displayEntry.entry_subtype_ids || []);
        setKeywordsArr(normalizeKeywords(displayEntry.keywords));
        setGalleryVersion((v) => v + 1);
      }
    })();
  }, [currentIndex, entries, token, setForm, hydrateEntryForDisplay]);

  useEffect(() => {
    if (!form?.id) return;
    if (!form?.isSensitive) return;

    refreshEntryAndGallery();
  }, [sensitiveKey, form?.id, form?.isSensitive, refreshEntryAndGallery]);

  useEffect(() => {
    if (!showVaultUnlock) return;
    if (!sensitiveKey) return;

    (async () => {
      try {
        setShowVaultUnlock(false);

        if (pendingSensitiveSave?.isNewEntry) {
          await resumePendingSensitiveSave(sensitiveKey);
          return;
        }

        if (form?.id && form?.isSensitive) {
          await refreshEntryAndGallery();
          setLastEntryId(form?.id);
          setIsPublic(form?.isPublic);
          setIsSensitive(form?.isSensitive);
          setIsEditing(true);
          setKeywordsArr(normalizeKeywords(form?.keywords));
        }
      } catch (err) {
        console.error('Failed after vault unlock', err);
      }
    })();
  }, [
    showVaultUnlock,
    sensitiveKey,
    pendingSensitiveSave,
    resumePendingSensitiveSave,
    form?.id,
    form?.isSensitive,
    form?.isPublic,
    form?.keywords,
    refreshEntryAndGallery,
  ]);

  const handleSensitiveToggle = () => {
    setIsSensitive((prev) => {
      const newValue = !prev;

      if (newValue && isPublic) {
        setIsPublic(false);
        setForm((f) => ({ ...f, isPublic: false, isSensitive: true }));
        notify.show(
          'Sensitive entries cannot be public. Public sharing has been turned off.'
        );
        return true;
      }

      setForm((f) => ({ ...f, isSensitive: newValue }));
      return newValue;
    });
  };

  const handlePublicToggle = () => {
    setIsPublic((prev) => {
      const newValue = !prev;

      if (newValue && isSensitive) {
        notify.show(
          'Sensitive entries cannot be public. Turn off Sensitive first.'
        );
        setForm((f) => ({ ...f, isPublic: false }));
        return false;
      }

      setForm((f) => ({ ...f, isPublic: newValue }));
      return newValue;
    });
  };

  useEffect(() => {
    const onChanged = (e) => {
      const changedId = e?.detail?.entryId;
      if (!changedId || !form?.id || changedId !== form.id) return;
      refreshEntryAndGallery();
    };
    window.addEventListener('attachments:changed', onChanged);
    return () => window.removeEventListener('attachments:changed', onChanged);
  }, [form?.id, refreshEntryAndGallery]);

  /** -------- Gallery images (all image attachments) -------- */
  const allImageAttachments = React.useMemo(() => {
    const entryImages = Array.isArray(form.imageUrls) ? form.imageUrls : [];
    const attachedFiles = Array.isArray(files) ? files : [];

    const isImageAttachment = (item = {}) => {
      const mimeType = String(
        item.mime_type ??
          item.mimeType ??
          item.content_type ??
          item.contentType ??
          item.type ??
          ''
      ).toLowerCase();

      if (mimeType.startsWith('image/')) return true;

      const isSensitiveClientImage =
        Boolean(
          item.sensitiveClientEncrypted ?? item.sensitive_client_encrypted
        ) && Boolean(item.hasSensitiveThumb ?? item.has_sensitive_thumb);

      if (isSensitiveClientImage) return true;

      const filename = String(
        item.filename ?? item.file_name ?? item.name ?? item.original_name ?? ''
      ).toLowerCase();

      return /\.(avif|bmp|gif|heic|heif|jpe?g|png|svg|webp)$/i.test(filename);
    };

    const byId = new Map();

    // Attachment order is the source of truth for gallery order.
    // Append any legacy imageUrls that are not present in the attachment list.
    [...attachedFiles.filter(isImageAttachment), ...entryImages].forEach(
      (item) => {
        const id = item?.id ?? item?.attachment_id ?? item?.attachmentId;
        if (id == null) return;

        const key = String(id);
        byId.set(key, {
          ...(byId.get(key) || {}),
          ...item,
          id,
          filename:
            item.filename ??
            item.file_name ??
            item.name ??
            item.original_name ??
            byId.get(key)?.filename,
          blurhash:
            item.blurhash ?? item.blur_hash ?? byId.get(key)?.blurhash ?? null,
        });
      }
    );

    return Array.from(byId.values());
  }, [form.imageUrls, files]);

  const [galleryImages, setGalleryImages] = React.useState([]);

  React.useEffect(() => {
    let cancelled = false;

    async function load() {
      const imgs = allImageAttachments;

      if (!imgs.length) {
        if (!cancelled) setGalleryImages([]);
        return;
      }

      const promises = imgs.map(async (img) => {
        try {
          if (form?.isSensitive || img?.sensitiveClientEncrypted) {
            if (!sensitiveKey) return null;
            const [thumbResult, fullResult] = await Promise.all([
              loadSensitiveAttachment({
                attachmentId: img.id,
                kind: 'thumb',
                token,
                vaultKey: sensitiveKey,
              }),
              loadSensitiveAttachment({
                attachmentId: img.id,
                kind: 'preview',
                token,
                vaultKey: sensitiveKey,
              }),
            ]);
            const thumbnailSrc = URL.createObjectURL(thumbResult.blob);
            const fullSrc = URL.createObjectURL(fullResult.blob);
            return {
              id: img.id,
              src: thumbnailSrc,
              fullSrc,
              alt:
                thumbResult.privateMetadata?.originalName ||
                form.title ||
                'Entry image',
              blurhash: null,
              sensitiveObjectUrls: [thumbnailSrc, fullSrc],
            };
          }

          const [thumbnailSrc, fullSrc] = await Promise.all([
            getSignedUrl(img.id, 'inline', token, { thumb: true }),
            getSignedUrl(img.id, 'inline', token),
          ]);

          return {
            id: img.id,
            src: thumbnailSrc,
            fullSrc,
            alt: img.filename || form.title || 'Entry image',
            blurhash: img.blurhash || null,
          };
        } catch (e) {
          console.error('gallery image failed', e);
          return null;
        }
      });

      const results = (await Promise.all(promises)).filter(Boolean);

      if (!cancelled) {
        setGalleryImages(results);
      }
    }

    load();

    return () => {
      cancelled = true;
      setGalleryImages((current) => {
        current.forEach((image) => {
          (image.sensitiveObjectUrls || []).forEach((url) =>
            URL.revokeObjectURL(url)
          );
        });
        return current;
      });
    };
  }, [
    allImageAttachments,
    token,
    galleryVersion,
    form.title,
    form?.isSensitive,
    sensitiveKey,
  ]);

  if (showNoEntriesState) {
    return (
      <div className="mydata-wrapper">
        <div style={emptyStateCardStyle}>
          <h2 style={emptyStateTitleStyle}>No entries yet</h2>
          <p style={emptyStateTextStyle}>
            You do not have any entries yet. Click below to create your first
            entry.
          </p>

          <div
            className="form-buttons"
            style={{ marginTop: 24, justifyContent: 'flex-start' }}
          >
            <AppButton
              variant="solid"
              size="sm"
              onPress={handleNew}
              style={{ width: 120 }}
            >
              New Entry
            </AppButton>
          </div>
        </div>
      </div>
    );
  }

  if (showEmptySearchState) {
    return (
      <div className="mydata-wrapper">
        <div style={emptyStateCardStyle}>
          <h2 style={emptyStateTitleStyle}>No matching entries</h2>
          <p style={emptyStateTextStyle}>
            Nothing matched your current search. Try clearing the search or
            create a new entry.
          </p>

          <div
            className="form-buttons"
            style={{ marginTop: 24, justifyContent: 'flex-start' }}
          >
            <AppButton
              variant="solid"
              size="sm"
              onPress={handleClearSearch}
              style={{ width: 120 }}
            >
              Clear Search
            </AppButton>

            <AppButton
              variant="solid"
              size="sm"
              onPress={handleNew}
              style={{ width: 120 }}
            >
              New Entry
            </AppButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mydata-wrapper">
      {form.dataOwnerId !== userId && (
        <div className="shared-user-info">
          <button
            type="button"
            className="shared-user-profile-button"
            onClick={() => setPublicProfileUserId(form.dataOwnerId)}
            aria-label={`View ${form.sharedName || 'user'} public profile`}
          >
            {form.shareProfilePicture && form.profilePicture && (
              <img
                src={buildAvatarUrl(form.profilePicture)}
                alt=""
                className="shared-profile-pic"
              />
            )}
            <span className="shared-user-name">{form.sharedName}</span>
          </button>
        </div>
      )}

      <button
        className="nav-arrow left"
        onClick={() =>
          handlePrevResult(
            currentIndex,
            setCurrentIndex,
            listPageNo,
            setListPageNo,
            fetchPage
          )
        }
        disabled={listPageNo === 0 && currentIndex === 0}
      >
        &larr;
      </button>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="userdata-form"
        autoComplete="off"
      >
        {readOnly && (
          <>
            <header className="entry-header">
              <div className="eh-left">
                <div className="eh-eyebrow">
                  {form.category ? (
                    <div className="pill-row">
                      <button
                        className="pill pill--category category-pill"
                        type="button"
                        aria-label={`View category ${form.category}`}
                        onClick={() => handleCategoryClick(form.category)}
                      >
                        <svg
                          className="pill__icon"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            d="M10 4l2 2h8v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6h6z"
                            fill="currentColor"
                          />
                        </svg>
                        {form.category}
                      </button>
                    </div>
                  ) : (
                    <span className="chip">Uncategorized</span>
                  )}

                  <span
                    className={`status ${
                      form.isPublic ? 'status--public' : 'status--private'
                    }`}
                  >
                    • {form.isPublic ? 'Public' : 'Private'}
                  </span>

                  {form.isSensitive && (
                    <>
                      <span> • </span>
                      <span
                        className="sensitive-badge"
                        title="This entry contains sensitive information and cannot be public"
                      >
                        <span className="icon">🔐</span>
                        Sensitive
                      </span>
                    </>
                  )}

                  {typeof form.updatedAt == 'string' && (
                    <TimeAgo iso={form.updatedAt} />
                  )}
                </div>

                <h1 className="eh-title">{form.title || 'Untitled'}</h1>

                {!!normalizeKeywords(form.keywords).length && (
                  <div className="pill-row">
                    {normalizeKeywords(form.keywords).map((k, i) => (
                      <button
                        key={`${k}-${i}`}
                        type="button"
                        onClick={() => handleKeywordClick(k)}
                        title={`Keyword: ${k}`}
                        aria-label={`Keyword: ${k}`}
                        className="pill pill--tag"
                        style={{
                          cursor: 'pointer',
                          marginRight: 6,
                          marginBottom: 6,
                        }}
                      >
                        {k}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="eh-right">
                {form.dataOwnerId !== userId && (
                  <div className="shared-user-info">
                    <button
                      type="button"
                      className="shared-user-profile-button"
                      onClick={() => setPublicProfileUserId(form.dataOwnerId)}
                      aria-label={`View ${
                        form.sharedName || 'user'
                      } public profile`}
                    >
                      {form.shareProfilePicture && form.profilePicture && (
                        <img
                          src={buildAvatarUrl(form.profilePicture)}
                          alt=""
                          className="shared-profile-pic"
                        />
                      )}
                      <span className="shared-user-name">
                        {form.sharedName}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </header>

            {!isNew && galleryImages.length > 0 && (
              <EntryImageGallery
                key={form.id || 'no-entry'}
                images={galleryImages}
              />
            )}
          </>
        )}

        {!readOnly && (
          <>
            <div className="form-row">
              <label className="form-label">
                Title<span className="required">*</span>
              </label>
              <input
                type="text"
                name="title"
                className="title-input"
                placeholder="Enter a title…"
                value={form.title}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row">
              <label className="form-label" htmlFor="entry-category">
                Category<span className="required">*</span>
              </label>
              <div className="category-autocomplete">
                <input
                  ref={categoryInputRef}
                  id="msdb-category-entry-field"
                  type="text"
                  name="msdb-category-entry-value"
                  className="form-input category-autocomplete__input"
                  placeholder="Enter or select a category…"
                  value={form.category}
                  maxLength={20}
                  onChange={handleCategoryChange}
                  onFocus={() => {
                    setCategoryOpen(true);
                    setCategoryActiveIndex(-1);
                  }}
                  onBlur={() => {
                    window.setTimeout(() => {
                      setCategoryOpen(false);
                      setCategoryActiveIndex(-1);
                    }, 120);
                  }}
                  onKeyDown={handleCategoryKeyDown}
                  autoComplete="new-password"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  data-form-type="other"
                  data-lpignore="true"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={categoryOpen}
                  aria-controls="entry-category-options"
                  aria-activedescendant={
                    categoryActiveIndex >= 0
                      ? `entry-category-option-${categoryActiveIndex}`
                      : undefined
                  }
                  required
                />

                {categoryOpen && (
                  <div
                    id="entry-category-options"
                    className="category-autocomplete__menu"
                    role="listbox"
                  >
                    {categoryLoading ? (
                      <div className="category-autocomplete__status">
                        Loading categories…
                      </div>
                    ) : filteredCategoryOptions.length > 0 ? (
                      filteredCategoryOptions.map((option, index) => (
                        <button
                          key={String(option.category).toLowerCase()}
                          id={`entry-category-option-${index}`}
                          type="button"
                          role="option"
                          aria-selected={categoryActiveIndex === index}
                          className={`category-autocomplete__option${
                            categoryActiveIndex === index ? ' is-active' : ''
                          }`}
                          onMouseDown={(e) => e.preventDefault()}
                          onMouseEnter={() => setCategoryActiveIndex(index)}
                          onClick={() => chooseCategory(option.category)}
                        >
                          {option.displayCategory}
                        </button>
                      ))
                    ) : form.category.trim() ? (
                      <div className="category-autocomplete__status">
                        New category — save to add it to your list
                      </div>
                    ) : (
                      <div className="category-autocomplete__status">
                        No saved categories yet
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">
                Keywords<span className="required">*</span>
              </label>
              <div
                style={chipInputWrap}
                onClick={() => kwInputRef.current && kwInputRef.current.focus()}
              >
                {keywordsArr.map((k, i) => (
                  <span key={`${k}-${i}`} style={chipStyle}>
                    {k}
                    <button
                      type="button"
                      aria-label={`Remove ${k}`}
                      onClick={() => handleKeywordRemove(i)}
                      style={chipRemoveStyle}
                    >
                      &times;
                    </button>
                  </span>
                ))}
                <input
                  ref={kwInputRef}
                  type="text"
                  value={kwText}
                  placeholder="Add keywords…"
                  onChange={(e) => setKwText(e.target.value)}
                  onKeyDown={handleKeywordKeyDown}
                  onBlur={handleKeywordBlur}
                  style={chipTextInputStyle}
                  name="keywords"
                />
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                marginBottom: 0,
              }}
            >
              <label>
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={handlePublicToggle}
                  style={{ marginRight: '0.4rem' }}
                />
                Public
              </label>

              <label>
                <input
                  type="checkbox"
                  checked={isSensitive}
                  onChange={handleSensitiveToggle}
                  style={{ marginRight: '0.4rem' }}
                />
                Mark as Sensitive
              </label>

              {isDesktopSyncAvailable && (
                <label>
                  <input
                    type="checkbox"
                    checked={keepSynced}
                    onChange={(e) => setKeepSynced(e.target.checked)}
                    style={{ marginRight: '0.4rem' }}
                  />
                  Keep newly added files synced from this computer
                </label>
              )}
            </div>

            <div style={{ paddingTop: 0 }}>
              <EntryTypesCard
                types={typesCatalog}
                entryTypeIds={entryTypeIds}
                entrySubtypeIds={entrySubtypeIds}
                profileTypeIds={profileTypeIds}
                profileSubtypeIds={profileSubtypeIds}
                useProfileDefaults={useProfileDefaults}
                onToggleUseDefaults={handleToggleUseDefaults}
                onChangeTypes={setEntryTypeIds}
                onChangeSubtypes={setEntrySubtypeIds}
              />
            </div>
          </>
        )}

        {showVaultSetup && (
          <div
            style={{
              padding: 20,
              border: '1px solid #ccc',
              marginBottom: 20,
              background: '#fff',
              borderRadius: 12,
            }}
          >
            {newRecoveryKey ? (
              // ✅ AFTER CREATION → SHOW RECOVERY KEY
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  border: '1px solid #facc15',
                  borderRadius: 8,
                  background: '#fef9c3',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  Save your recovery key
                </div>

                <div
                  style={{
                    fontFamily: 'monospace',
                    fontSize: 14,
                    marginBottom: 10,
                    wordBreak: 'break-word',
                  }}
                >
                  {newRecoveryKey}
                </div>

                <button type="button" onClick={handleCopyRecoveryKey}>
                  Copy Recovery Key
                </button>
                <label style={{ display: 'block', marginTop: 12 }}>
                  <input
                    type="checkbox"
                    checked={recoveryKeyConfirmed}
                    onChange={(e) => setRecoveryKeyConfirmed(e.target.checked)}
                    style={{ marginRight: 8 }}
                  />
                  I saved this recovery key
                </label>

                {copyMessage ? (
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    {copyMessage}
                  </div>
                ) : null}

                <div style={{ marginTop: 12 }}>
                  <button
                    type="button"
                    disabled={!recoveryKeyConfirmed}
                    onClick={async () => {
                      setShowVaultSetup(false);
                      await resumePendingSensitiveSave(sensitiveKey);
                      setNewRecoveryKey('');
                      setCopyMessage('');
                      notify.show('Vault created and sensitive entry saved.');
                    }}
                  >
                    Continue
                  </button>
                </div>
              </div>
            ) : (
              // ✅ BEFORE CREATION → SHOW FORM
              <>
                <p style={{ marginBottom: 12 }}>
                  Create a passphrase to protect your sensitive entries.
                </p>

                <input
                  type="password"
                  placeholder="Enter passphrase"
                  value={vaultPass}
                  onChange={(e) => setVaultPass(e.target.value)}
                  style={{ width: '100%', marginBottom: 8 }}
                />

                <input
                  type="password"
                  placeholder="Confirm passphrase"
                  value={vaultPassConfirm}
                  onChange={(e) => setVaultPassConfirm(e.target.value)}
                  style={{ width: '100%', marginBottom: 12 }}
                />

                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!vaultPass || vaultPass !== vaultPassConfirm) {
                        alert('Passphrases must match');
                        return;
                      }

                      try {
                        const result = await setupVault({
                          passphrase: vaultPass,
                        });

                        setNewRecoveryKey(result.recoveryKey || '');
                        setCopyMessage('');
                        setVaultPass('');
                        setVaultPassConfirm('');
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                  >
                    Create Vault
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setShowVaultSetup(false);
                      setVaultPass('');
                      setVaultPassConfirm('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {(showVaultUnlock || shouldShowReadOnlyVaultUnlock) &&
          !isVaultUnlocked && (
            <div style={{ marginBottom: 20 }}>
              <VaultUnlockPanel />
            </div>
          )}

        <div className="detail-wrapper">
          <div className="form-row form-row-textarea">
            {readOnly ? (
              shouldShowReadOnlyVaultUnlock ? (
                <div
                  className="readonly-textarea-display"
                  style={{
                    padding: 16,
                    border: '1px solid #dbe4f0',
                    borderRadius: 12,
                    background: '#f8fafc',
                    color: '#475569',
                    fontStyle: 'italic',
                  }}
                >
                  Sensitive entry locked
                </div>
              ) : (
                <div
                  className="readonly-textarea-display"
                  dangerouslySetInnerHTML={{ __html: finalHtml }}
                />
              )
            ) : (
              <>
                <label className="detail-label">
                  DETAIL<span className="required">*</span>
                </label>
                <div
                  className="detail-wrapper"
                  onKeyDownCapture={(e) => {
                    if (
                      e.key === '/' &&
                      !e.ctrlKey &&
                      !e.metaKey &&
                      !e.altKey
                    ) {
                      e.stopPropagation();
                    }
                  }}
                >
                  <RichTextEditor
                    html={editorHtml}
                    placeholder="Add details, notes, or other information…"
                    onChange={(nextHtml) => {
                      setEditorHtml(nextHtml);

                      setForm((prev) => ({
                        ...prev,
                        detailHtml: nextHtml,
                        detail: htmlToPlainTextWeb(nextHtml),
                      }));
                    }}
                    className="form-richtext"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {isEditing && (
          <div
            {...rootProps}
            onPaste={handlePaste}
            onClick={() => dropzoneRef.current?.focus()}
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <p>Drop the files here…</p>
            ) : (
              <>
                <p>
                  Drag & drop files here, <br />
                  click the button, or paste an image
                </p>
                <button
                  className="browse-files-button"
                  type="button"
                  onClick={async () => {
                    if (keepSynced && window.syncApi?.selectFilesForSync) {
                      const selected =
                        await window.syncApi.selectFilesForSync();

                      console.log('[sync-picker] selected files:', selected);

                      const pickedFiles = selected.map((item) => {
                        const binary = atob(item.dataBase64);
                        const bytes = new Uint8Array(binary.length);

                        for (let i = 0; i < binary.length; i++) {
                          bytes[i] = binary.charCodeAt(i);
                        }

                        const file = new File([bytes], item.name, {
                          lastModified: item.lastModified,
                        });

                        file.localPath = item.localPath;
                        file.uploadName = item.name;

                        return file;
                      });

                      const { allowed, rejected } = partitionFilesBySize(
                        pickedFiles,
                        pendingFiles
                      );

                      if (allowed.length > 0) {
                        setPendingFiles((prev) =>
                          addFilesWithUniqueNames(allowed, prev)
                        );
                      }

                      if (rejected.length > 0) {
                        const names = rejected
                          .map((r) => r.file.name)
                          .join(', ');
                        setUploadWarning(
                          `Some files were too large and weren’t added: ${names}.`
                        );
                      } else {
                        setUploadWarning('');
                      }

                      return;
                    }

                    open();
                  }}
                >
                  Browse Files
                </button>
              </>
            )}
            {filesToUpload.length > 0 && (
              <ul className="upload-list">
                {filesToUpload.map((f, i) => (
                  <li key={i}>{f.name}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {isEditing && uploadWarning && (
          <div className="upload-warning">{uploadWarning}</div>
        )}

        {(pendingFiles.length > 0 || pendingLinks.length > 0) && (
          <div className="file-preview-list">
            <strong>Files ready to upload:</strong>
            <ul>
              {pendingFiles.map((file, idx) => (
                <li key={`file-${idx}`}>
                  {file.name} ({Math.round(file.size / 1024)} KB)
                </li>
              ))}
              {pendingLinks.map((url, idx) => (
                <li key={`link-${idx}`}>
                  {detectLinkKind(url) === 'youtube' ? 'YouTube link' : 'Link'}:{' '}
                  {url}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="form-buttons">
          {/* Owner-only buttons */}
          {form.dataOwnerId === userId && (
            <>
              {isEditing ? (
                <>
                  <AppButton
                    variant="solid"
                    size="sm"
                    onPress={handleSubmit}
                    style={{ width: 90 }}
                  >
                    Save
                  </AppButton>
                  <AppButton
                    variant="solid"
                    size="sm"
                    onPress={handleCancel}
                    style={{ width: 90 }}
                  >
                    Cancel
                  </AppButton>
                </>
              ) : (
                <>
                  <AppButton
                    variant="solid"
                    size="sm"
                    type="button"
                    onPress={handleEdit}
                    style={{ width: 90 }}
                    disabled={!form?.id}
                  >
                    Edit
                  </AppButton>

                  <AppButton
                    variant="solid"
                    size="sm"
                    onPress={handleDelete}
                    style={{ width: 90 }}
                    disabled={!form?.id}
                  >
                    Delete
                  </AppButton>
                </>
              )}
            </>
          )}

          {!isEditing && (
            <AppButton
              variant="solid"
              size="sm"
              onPress={handleNew}
              style={{ width: 90 }}
            >
              New
            </AppButton>
          )}
        </div>
      </form>

      <button
        className="nav-arrow right"
        onClick={() =>
          handleNextResult(
            currentIndex,
            setCurrentIndex,
            entries,
            listPageNo,
            setListPageNo,
            fetchPage
          )
        }
        disabled={
          currentIndex + listPageNo * itemsPerPage >= totalEntriesFound - 1
        }
      >
        &rarr;
      </button>

      <span className="entry-counter">
        {totalEntriesFound > 0
          ? `${
              currentIndex + 1 + listPageNo * itemsPerPage
            } of ${totalEntriesFound}`
          : '0 of 0'}
      </span>

      {publicProfileUserId && (
        <SharedPublicProfileModal
          userId={publicProfileUserId}
          token={token}
          onClose={() => setPublicProfileUserId(null)}
        />
      )}
    </div>
  );
}
